import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  CreateEgressProfileRequest,
  EgressProfileRecord,
  UpdateEgressProfileRequest,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  createEgressProfile,
  deleteEgressProfile,
  listEgressProfiles,
  updateEgressProfile,
} from './egressProfiles.ts';

export const EGRESS_PROFILES_QUERY_KEY = ['egress-profiles'] as const;

export function useEgressProfiles(): UseQueryResult<EgressProfileRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: EGRESS_PROFILES_QUERY_KEY,
    queryFn: ({ signal }) => listEgressProfiles(fetchImpl, signal),
  });
}

export function useCreateEgressProfile(): UseMutationResult<
  EgressProfileRecord,
  Error,
  CreateEgressProfileRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createEgressProfile(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_PROFILES_QUERY_KEY });
    },
  });
}

export function useUpdateEgressProfile(): UseMutationResult<
  EgressProfileRecord,
  Error,
  { profile_id: string } & UpdateEgressProfileRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profile_id, ...body }) =>
      updateEgressProfile(profile_id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_PROFILES_QUERY_KEY });
    },
  });
}

export function useDeleteEgressProfile(): UseMutationResult<void, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (profileId) => {
      await deleteEgressProfile(profileId, fetchImpl);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EGRESS_PROFILES_QUERY_KEY });
    },
  });
}
